import { Moon, Sun } from 'lucide-react'
import { motion } from 'framer-motion'
import { cn } from '@/utils/cn'
import { isRocketInstance } from '@/config/instanceProfile'

type ThemeModeToggleProps = {
  isDark: boolean
  onToggle: () => void
  className?: string
}

const KNOB_TRAVEL = 22

export function ThemeModeToggle({
  isDark,
  onToggle,
  className,
}: ThemeModeToggleProps) {
  const stateLabel = isDark ? 'Modo escuro ativo' : 'Modo claro ativo'
  const actionHint = isDark ? 'Alternar para modo claro' : 'Alternar para modo escuro'
  const rocket = isRocketInstance

  return (
    <div className={cn('theme-toggle group flex justify-center py-0.5', className)}>
      <button
        type='button'
        role='switch'
        aria-checked={isDark}
        aria-label={`${stateLabel}. ${actionHint}`}
        title={actionHint}
        onClick={onToggle}
        className={cn(
          'relative h-8 w-[52px] shrink-0 overflow-hidden rounded-full p-0.5',
          'transition-[box-shadow,transform] duration-200',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2',
          'active:scale-[0.98]',
          isDark
            ? rocket
              ? [
                  'bg-[#1a1b1f]',
                  'shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_2px_10px_rgba(0,0,0,0.35),0_0_14px_rgba(212,20,90,0.32)]',
                  'group-hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_2px_12px_rgba(0,0,0,0.4),0_0_22px_rgba(212,20,90,0.45),0_0_32px_rgba(232,58,116,0.22)]',
                  'focus-visible:ring-offset-[#0a0b0c]',
                ]
              : [
                  'bg-[#1a1b1f]',
                  'shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_2px_10px_rgba(0,0,0,0.35),0_0_14px_rgba(129,140,248,0.28)]',
                  'group-hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_2px_12px_rgba(0,0,0,0.4),0_0_22px_rgba(129,140,248,0.45),0_0_32px_rgba(167,139,250,0.2)]',
                  'focus-visible:ring-offset-[#0a0b0c]',
                ]
            : [
                'bg-white',
                'shadow-[inset_0_1px_2px_rgba(15,23,42,0.06),0_2px_12px_rgba(15,23,42,0.08),0_0_14px_rgba(251,191,36,0.32)]',
                'group-hover:shadow-[inset_0_1px_2px_rgba(15,23,42,0.06),0_2px_14px_rgba(15,23,42,0.1),0_0_22px_rgba(251,191,36,0.5),0_0_30px_rgba(251,146,60,0.22)]',
                'focus-visible:ring-offset-white',
              ],
        )}
      >
        <motion.span
          layout
          transition={{ type: 'spring', stiffness: 520, damping: 34 }}
          className={cn(
            'pointer-events-none absolute top-1/2 h-7 w-7 -translate-y-1/2 rounded-full blur-[6px]',
            isDark
              ? rocket
                ? 'bg-[#d4145a]/55'
                : 'bg-indigo-400/55'
              : 'bg-amber-300/70',
          )}
          style={{ left: isDark ? KNOB_TRAVEL + 1 : 1 }}
          aria-hidden
        />

        <span
          className={cn(
            'pointer-events-none absolute inset-0 rounded-full',
            isDark
              ? rocket
                ? 'bg-gradient-to-r from-[#d4145a]/20 via-transparent to-[#e83a74]/25 opacity-100'
                : 'bg-gradient-to-r from-indigo-500/15 via-transparent to-violet-500/20 opacity-100'
              : 'bg-gradient-to-r from-sky-200/60 via-transparent to-amber-200/80 opacity-100',
          )}
          aria-hidden
        />

        <motion.span
          layout
          transition={{ type: 'spring', stiffness: 520, damping: 34 }}
          className={cn(
            'relative z-[1] flex h-7 w-7 items-center justify-center rounded-full',
            isDark
              ? rocket
                ? 'bg-gradient-to-br from-[#2a1a22] via-[#1e2028] to-[#2a1520] shadow-[0_2px_8px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.08)]'
                : 'bg-gradient-to-br from-[#2a2d38] via-[#1e2028] to-[#252238] shadow-[0_2px_8px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.08)]'
              : 'bg-gradient-to-br from-[#dbeafe] via-white to-[#ffedd5] shadow-[0_2px_8px_rgba(148,163,184,0.35),inset_0_1px_0_rgba(255,255,255,0.95)]',
          )}
          style={{ x: isDark ? KNOB_TRAVEL : 0 }}
        >
          {isDark ? (
            <Moon
              size={14}
              className={
                rocket
                  ? 'text-[#f9a8c4] drop-shadow-[0_0_6px_rgba(212,20,90,0.55)]'
                  : 'text-indigo-200/90 drop-shadow-[0_0_6px_rgba(129,140,248,0.55)]'
              }
              strokeWidth={2.25}
            />
          ) : (
            <Sun
              size={15}
              className='text-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.65)]'
              strokeWidth={2.25}
            />
          )}
        </motion.span>
      </button>
    </div>
  )
}
